import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api/api';

export default function HelpPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    fetchHelpPages();
  }, []);

  useEffect(() => {
    if (pageId && pages.length > 0) {
      const page = pages.find(p => p.PageId === parseInt(pageId));
      if (page) {
        setSelectedPage(page);
      }
    } else if (pages.length > 0 && !selectedPage) {
      setSelectedPage(pages[0]);
    }
  }, [pageId, pages]);

  const fetchHelpPages = async () => {
    try {
      const response = await api.GetHelpPages();
      const helpPages = response.GetHelpPagesjs?.Table || [];
      const sortedPages = helpPages
        .filter(page => page.isvisible === 1 && page.PageImage && page.PageImage.trim() !== '')
        .sort((a, b) => a.PageSeqNo - b.PageSeqNo);
      setPages(sortedPages);
      if (sortedPages.length > 0 && !pageId) {
        setSelectedPage(sortedPages[0]);
      }
    } catch (error) {
      console.error('Error fetching help pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
    setShowMenu(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  const BackButton = ({ className = '' }) => (
    <button
      type="button"
      onClick={handleBack}
      className={`inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${className}`}
    >
      <span aria-hidden="true">&larr;</span>
      <span>Back</span>
    </button>
  );

  const MenuList = ({ isModal = false }) => (
    <div className={`${isModal ? 'h-full' : 'h-full'} bg-white border-r border-gray-200`}>
      <div className="flex items-center justify-between p-4 bg-red-600">
        <div className="flex items-center">
          <span className="mr-3 text-white">📚</span>
          <h2 className="font-semibold text-white">Information</h2>
        </div>
        {isModal && (
          <button
            onClick={() => setShowMenu(false)}
            className="text-white hover:text-gray-200"
          >
            ✕
          </button>
        )}
      </div>
      <nav className="divide-y divide-gray-200 overflow-y-auto h-[calc(100%-60px)]">
        {pages.map((page) => (
          <Link
            key={page.PageId}
            to={`/help/${page.PageId}`}
            onClick={() => handlePageSelect(page)}
            className={`block px-4 py-3 hover:bg-red-50 transition-colors ${
              selectedPage?.PageId === page.PageId
                ? 'bg-red-50 text-red-700 font-medium'
                : 'text-gray-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="flex items-center justify-center w-8 h-8 mr-3 bg-gray-100 rounded">
                  📄
                </span>
                <span className="flex-1">{page.MenuName}</span>
              </div>
              {selectedPage?.PageId === page.PageId && (
                <span className="text-red-600">✓</span>
              )}
            </div>
          </Link>
        ))}
      </nav>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen app-peach-bg">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto border-b-2 border-red-600 rounded-full animate-spin"></div>
          <p className="mt-2 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Mobile Layout with Modal Menu
  if (isMobile) {
    return (
      <div className="min-h-screen app-peach-bg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <button type="button" onClick={handleBack} className="p-2 text-red-600 rounded-full bg-orange-50">
            ←
          </button>
          <h1 className="text-lg font-bold text-gray-800">Terms & Conditions</h1>
          <button
            onClick={() => setShowMenu(true)}
            className="p-2 text-gray-700 bg-gray-100 rounded-full"
          >
            ☰
          </button>
        </div>

        {/* Current Selection Indicator */}
        {selectedPage && (
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <div className="flex items-center">
              <span className="mr-2 text-red-600">📄</span>
              <span className="flex-1 mr-4 font-medium text-gray-700 truncate">
                {selectedPage.MenuName}
              </span>
            </div>
            <button
              onClick={() => setShowMenu(true)}
              className="text-red-600"
            >
              ⋮
            </button>
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {selectedPage ? (
            <div className="p-6 bg-white rounded-lg shadow-sm">
              <h2 className="mb-4 text-xl font-bold text-gray-800">{selectedPage.PageTitle}</h2>
              <div className="w-16 h-1 mb-6 bg-red-600"></div>
              <p className="mb-6 leading-relaxed text-gray-600">{selectedPage.PageContent}</p>
              {selectedPage.PageImage && (
                <div className="mt-6 overflow-hidden border border-gray-200 rounded-lg">
                  <img
                    src={`/assets/Help/${selectedPage.PageImage}`}
                    alt={selectedPage.PageTitle}
                    className="object-contain w-full h-48"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <span className="mb-4 text-6xl">ℹ️</span>
              <p>Select a menu item to view content</p>
            </div>
          )}
        </div>

        {/* Modal Menu for Mobile */}
        {showMenu && (
          <div className="fixed inset-0 z-50">
            <div 
              className="absolute inset-0 bg-black bg-opacity-40"
              onClick={() => setShowMenu(false)}
            ></div>
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70%]">
              <MenuList isModal={true} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop/Tablet Layout - Side by side
  return (
    <div className="min-h-[calc(100vh-200px)]">
      <div className="flex flex-col gap-4 md:flex-row">
        {/* Sidebar - Menu List */}
        <div className="w-full md:w-1/3 lg:w-1/4">
          <MenuList />
        </div>

        {/* Main Content - Page Details */}
        <div className="w-full md:w-2/3 lg:w-3/4">
          {selectedPage ? (
            <div className="p-6 bg-white rounded-lg shadow-sm">
              <div className="flex justify-end mb-4">
                <BackButton />
              </div>
              <h2 className="mb-4 text-xl font-bold text-gray-800">{selectedPage.PageTitle}</h2>
              <div className="w-16 h-1 mb-6 bg-red-600"></div>
              <p className="mb-6 leading-relaxed text-gray-600">{selectedPage.PageContent}</p>
              {selectedPage.PageImage && (
                <div className="mt-6 overflow-hidden border border-gray-200 rounded-lg">
                  <img
                    src={`/assets/Help/${selectedPage.PageImage}`}
                    alt={selectedPage.PageTitle}
                    className="object-contain w-full h-48"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <span className="mb-4 text-6xl">ℹ️</span>
              <p>Select a menu item to view content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
